import type { Channel, Program } from "@hybrid-iptv/types";
import { useEffect, useRef, useState } from "react";
import { useDialogFocus } from "@hybrid-iptv/ui";
import { genreColor } from "./genreColors.js";
import { useNotifications } from "../library/notificationsStore.js";
import { useFavoritesStore } from "../library/favoritesStore.js";
import { startWebRecording, useRecordAction } from "../dvr/recordAction.js";

interface MiniEpgProps {
  channel: Channel | null;
  programs: Program[];
  visible: boolean;
  onClose: () => void;
  /** Sparkle-style: stay in the guide to preview, or jump to fullscreen now. */
  onGoLive: () => void;
  /** Android: book the recording through the DVR dialog (same as the traditional layout). */
  onRecord?: (program: Program) => void;
}

/**
 * Inline preview card shown when a channel or a programme is selected: the
 * programme's details (title, description, times) plus Go Live, Record and
 * Track. It goes up on OK/click — never on merely highlighting a guide row —
 * and holds focus while it is up, because on a remote there is no pointer: its
 * buttons have to be reachable with the D-pad. Back/Escape dismisses it and
 * hands focus back to the guide element that opened it (owner, 2026-09-21).
 *
 * It carries the same actions the traditional layout's detail dialog does, so
 * the two layouts offer the same things on a show; only the SIZE differs.
 */
export function MiniEpg({ channel, programs, visible, onClose, onGoLive, onRecord }: MiniEpgProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  // Shown only when there is something to describe — the focus hook must never
  // run against an empty card, or it would focus the first control on the page.
  const shown = visible && !!channel && programs.length > 0;
  // Modal for the D-pad (spatialNav scopes arrows to an open dialog, so Left /
  // Right walk its buttons) while Back/Escape still closes it.
  useDialogFocus({ open: shown, onClose, rootRef: cardRef });

  // No auto-dismiss: this card carries actionable buttons, so it stays until
  // the viewer acts (dismiss / Go Live). It is still cheap — it re-renders with
  // the new channel on every CH+/− zap.
  useEffect(() => {
    if (visible && !channel) onClose();
  }, [visible, channel, onClose]);

  if (!shown) return null;

  return (
    <MiniEpgCard
      channel={channel}
      program={programs[0]!}
      cardRef={cardRef}
      onClose={onClose}
      onGoLive={onGoLive}
      onRecord={onRecord}
    />
  );
}

interface MiniEpgCardProps {
  channel: Channel;
  program: Program;
  cardRef: React.RefObject<HTMLDivElement | null>;
  onClose: () => void;
  onGoLive: () => void;
  onRecord?: (program: Program) => void;
}

/** The card itself — split out so the record/track hooks below only ever run
 *  with a real programme (the surface above may be closed). */
function MiniEpgCard({ channel, program, cardRef, onClose, onGoLive, onRecord }: MiniEpgCardProps) {
  // Section 12 title tracking — "track" a programme to be alerted when its
  // title airs on any channel.
  const tracked = useNotifications((s) => s.isTracked(program.title));
  const addTrackedTitle = useNotifications((s) => s.addTrackedTitle);
  const removeTrackedTitle = useNotifications((s) => s.removeTrackedTitle);
  const record = useRecordAction(program, channel);
  const [recordHint, setRecordHint] = useState(false);
  // Favorites are per CHANNEL (see ProgramContextMenu): the programme is gone
  // on the next guide refresh, the channel is what the viewer wants back.
  const isFavorite = useFavoritesStore((s) => s.isFavorite("channel", channel.id));
  const toggleFavorite = useFavoritesStore((s) => s.toggleFavorite);

  const color = genreColor(program.genre);
  const start = formatTime(program.start);
  const end = formatTime(program.end);
  const now = Date.now();
  const isLive = now >= Date.parse(program.start) && now < Date.parse(program.end);
  const canRecord = Date.parse(program.end) > now;

  return (
    <div className="epg-mini-overlay" onClick={onClose}>
      <div
        ref={cardRef}
        className="epg-mini"
        role="dialog"
        // Marks this dialog as the NON-modal one: the guide's CH+/− zap still
        // works while it holds focus (LivePage's key handler keys off this).
        data-epg-mini=""
        aria-label={`${channel.number ? `${channel.number} ` : ""}${channel.name}: ${program.title}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="epg-mini-header">
          <span className="epg-mini-channel">{channel.number ? `${channel.number} ` : ""}{channel.name}</span>
          {isLive && <span className="epg-mini-live">LIVE</span>}
          {program.genre && (
            <span className="epg-mini-genre" style={{ color }}>{program.genre}</span>
          )}
        </div>
        <h3 className="epg-mini-title">{program.title}</h3>
        {program.subtitle && <p className="epg-mini-subtitle">{program.subtitle}</p>}
        <div className="epg-mini-time">
          <span>{start} – {end}</span>
        </div>
        {program.description && <p className="epg-mini-desc">{program.description}</p>}
        <div className="epg-mini-actions">
          <button className="epg-mini-golive" onClick={onGoLive}>
            ▶ Go Live
          </button>
          <div className="epg-mini-secondary">
            {canRecord && (record.kind === "dvr" && onRecord ? (
              <button className="epg-mini-action" title={record.title} onClick={() => onRecord(program)}>
                <span className="rec-dot" aria-hidden="true" /> Record
              </button>
            ) : record.kind === "now" ? (
              <button className="epg-mini-action" title={record.title} onClick={() => void startWebRecording(program, channel, onGoLive)}>
                <span className="rec-dot" aria-hidden="true" /> Record now
              </button>
            ) : (
              // Greyed out, but a tap (no hover on a phone or TV) still says
              // where scheduled recording works.
              <button className="epg-mini-action epg-mini-record--off" aria-disabled="true" title={record.title} onClick={() => setRecordHint(true)}>
                <span className="rec-dot rec-dot--off" aria-hidden="true" /> Record
              </button>
            ))}
            <button
              className="epg-mini-action"
              aria-pressed={tracked}
              onClick={() => (tracked ? removeTrackedTitle(program.title) : addTrackedTitle(program.title))}
            >
              {tracked ? "✓ Tracking" : "🔔 Track"}
            </button>
            <button
              className="epg-mini-action"
              aria-pressed={isFavorite}
              aria-label={isFavorite ? "Remove channel from favorites" : "Add channel to favorites"}
              onClick={() => toggleFavorite("channel", channel.id)}
            >
              {isFavorite ? "★ Favorite" : "☆ Favorite"}
            </button>
            <button className="epg-mini-action" onClick={onClose}>Close</button>
          </div>
        </div>
        {recordHint && <p className="epg-mini-record-hint" role="status">{record.title}.</p>}
      </div>
    </div>
  );
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${m}${ampm}`;
}
